import { SURF_APP_ID } from "../constants.js";
import { SURF_SPLIT_SYSTEM, buildResearchChatPrompt, buildSourceCleanPrompt, inferSourceTitle, surfSplitUserPrompt, } from "../outline/outlinePrompt.js";
import { parseOutlineSections } from "../outline/parseOutline.js";
import { SURF_AGENT_MODEL_ID, SurfCloudAPI, } from "../graphql/surfApi.js";
import { pollSurfAIJob } from "./surfAiJob.js";
import { surfBedrockChat } from "./surfBedrockChat.js";
async function submitBedrockJob(cfg, feature, prompt, systemText, userText, maxTokens = 4096) {
    await SurfCloudAPI.ensureProfile(cfg);
    const result = await SurfCloudAPI.submitAIJob(cfg, {
        kind: "BEDROCK_CONVERSE",
        provider: "BEDROCK",
        prompt,
        modelId: SURF_AGENT_MODEL_ID,
        feature,
        paramsJson: JSON.stringify({
            bedrock: { systemText, userText, maxTokens },
        }),
        inputAssetKeys: [],
    });
    return pollSurfAIJob(cfg, result.jobId);
}
export async function cleanSurfSource(cfg, raw) {
    const { systemText, userText } = buildSourceCleanPrompt(raw);
    return surfBedrockChat(cfg, {
        feature: "surf.source.clean",
        systemText,
        userText,
        maxTokens: 8192,
    });
}
export async function researchSurfSource(cfg, sourceText, question, history) {
    const { systemText, userText } = buildResearchChatPrompt(sourceText, question, history);
    return surfBedrockChat(cfg, {
        feature: "surf.research.chat",
        systemText,
        userText,
        maxTokens: 2048,
    });
}
export async function splitSurfOutline(cfg, sourceText, presentationType, slideTarget = "auto") {
    const title = inferSourceTitle(sourceText);
    const userText = surfSplitUserPrompt(title, sourceText, {
        presentationType,
        slideTarget,
    });
    // Outline is inherently long (more so with a high slide count) and exceeds the
    // ~29s sync proxy limit → 503. Go async directly (submit AI_JOB + poll), no sync try.
    // Scale output budget with slide count so the JSON isn't truncated (→ parse error).
    // Claude Haiku 4.5 supports large max_tokens.
    const count = typeof slideTarget === "number" ? slideTarget : 0;
    const maxTokens = count > 0 ? Math.min(32000, 4000 + count * 900) : 12000;
    const raw = await submitBedrockJob(cfg, "surf.outline.split", "Genera outline", SURF_SPLIT_SYSTEM, userText, maxTokens);
    return parseOutlineSections(raw);
}
function webProxyPath(baseUrl, path) {
    const base = baseUrl.replace(/\/+$/, "");
    return base ? `${base}${path}` : path;
}
async function webProxyFetch(proxy, path, feature, body) {
    const token = await proxy.getIdToken();
    const url = webProxyPath(proxy.baseUrl, path);
    try {
        return await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "x-oidc-token": token,
                "X-Vida-AppId": SURF_APP_ID,
                "X-Vida-Feature": feature,
            },
            body: JSON.stringify(body),
        });
    }
    catch (e) {
        if (e instanceof TypeError) {
            throw new Error("Impossibile contattare il proxy web (rete o CORS). " +
                "Rigenera .env con surf-web-sync-env.sh e riavvia il dev server.");
        }
        throw e;
    }
}
export async function extractWebUrl(proxy, url) {
    const res = await webProxyFetch(proxy, "/web/extract", "surf.web.extract", { url });
    const text = await res.text();
    if (!res.ok) {
        throw new Error(`Estrazione URL (${res.status}): ${text.slice(0, 200)}`);
    }
    const json = JSON.parse(text);
    if (json.error)
        throw new Error(json.error);
    const markdown = String(json.markdown ?? "").trim();
    if (!markdown)
        throw new Error("Nessun contenuto estratto dall'URL.");
    return { markdown, title: json.title };
}
export async function searchWeb(proxy, query, maxResults = 6) {
    const res = await webProxyFetch(proxy, "/web/search", "surf.web.search", {
        query,
        maxResults,
    });
    const text = await res.text();
    if (!res.ok) {
        throw new Error(`Ricerca web (${res.status}): ${text.slice(0, 200)}`);
    }
    const json = JSON.parse(text);
    return (json.results ?? []).map((r) => ({
        title: String(r.title ?? ""),
        url: String(r.url ?? ""),
        content: String(r.content ?? ""),
    }));
}
function normalizeRagBase(raw) {
    let base = raw.replace(/\/+$/, "");
    for (const suffix of ["/rag/search", "/rag/reindex-pdf", "/rag/index"]) {
        if (base.endsWith(suffix))
            base = base.slice(0, -suffix.length).replace(/\/+$/, "");
    }
    return base;
}
async function ragPost(cfg, path, body) {
    const token = await cfg.getIdToken();
    const url = `${normalizeRagBase(cfg.ragBaseUrl)}${path}`;
    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-oidc-token": token,
        },
        body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok)
        throw new Error(`RAG ${path} (${res.status}): ${text.slice(0, 200)}`);
    return JSON.parse(text);
}
async function requestPdfUploadUrl(cfg, file) {
    const base = cfg.presignedBaseUrl.replace(/\/+$/, "");
    const token = await cfg.getIdToken();
    const res = await fetch(`${base}/blob/upload-url`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            kind: "pdf",
            filename: file.name,
            contentLength: file.size,
            contentType: file.type || "application/pdf",
            ownerId: "surf",
        }),
    });
    const text = await res.text();
    if (!res.ok)
        throw new Error(`Upload URL (${res.status}): ${text.slice(0, 200)}`);
    const json = JSON.parse(text);
    if (!json.url || !json.s3Key)
        throw new Error("Risposta upload non valida.");
    return { url: json.url, s3Key: json.s3Key };
}
async function pollPdfIndexed(cfg, noteId, timeoutMs = 120_000) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        const result = await ragPost(cfg, "/rag/search", {
            query: "contenuto documento",
            scope: "document",
            scopeId: noteId,
            limit: 4,
        });
        if (result.chunks?.length)
            return;
        await new Promise((r) => setTimeout(r, 2500));
    }
    throw new Error("Timeout indicizzazione PDF.");
}
async function gatherPdfText(cfg, noteId) {
    const queries = [
        "contenuto principale",
        "introduzione",
        "conclusione",
        "capitolo sezione",
        "riassunto",
    ];
    const seen = new Set();
    const parts = [];
    for (const query of queries) {
        const result = await ragPost(cfg, "/rag/search", {
            query,
            scope: "document",
            scopeId: noteId,
            limit: 12,
        });
        for (const chunk of result.chunks ?? []) {
            const t = chunk.text?.trim();
            if (!t || seen.has(t))
                continue;
            seen.add(t);
            parts.push(t);
        }
    }
    if (!parts.length)
        throw new Error("Nessun testo estratto dal PDF.");
    return parts.join("\n\n");
}
/** Upload PDF, index via pdf-ingest, return extracted text. */
export async function extractPdfText(cfg, file, sessionNoteId) {
    const presigned = await requestPdfUploadUrl(cfg, file);
    const putRes = await fetch(presigned.url, {
        method: "PUT",
        headers: {
            "Content-Type": file.type || "application/pdf",
            "Content-Length": String(file.size),
        },
        body: file,
    });
    if (!putRes.ok)
        throw new Error(`Upload PDF (${putRes.status})`);
    await ragPost(cfg, "/rag/reindex-pdf", {
        noteId: sessionNoteId,
        pdfS3Key: presigned.s3Key,
    });
    await pollPdfIndexed(cfg, sessionNoteId);
    return gatherPdfText(cfg, sessionNoteId);
}
